/**
 * CR-6256: Platform Admin → Regional Defaults.
 *
 * Sets the platform-wide currency, timezone and date format — the floor of the
 * Platform → Group → Site cascade. These apply only where a site holds no
 * override and no ancestor group sets a value. Before this they were constants
 * compiled into four separate resolvers, so "Platform Default" named something
 * nobody could change.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Globe, Loader2, Save, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { platformSettingsAPI, preferencesAPI } from '../../../lib/api';
import type { PreferencesChoices } from '../../../lib/api';
import { formatDatePreview } from './formatDatePreview';

interface RegionalDefaults {
  default_currency: string;
  default_timezone: string;
  default_date_format: string;
}

const EMPTY: RegionalDefaults = {
  default_currency: '',
  default_timezone: '',
  default_date_format: '',
};

export function RegionalDefaultsTab() {
  const [values, setValues] = useState<RegionalDefaults>(EMPTY);
  const [initial, setInitial] = useState<RegionalDefaults>(EMPTY);
  const [choices, setChoices] = useState<PreferencesChoices | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDefaults = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [settingsRes, choicesRes] = await Promise.all([
        platformSettingsAPI.getSettings(),
        preferencesAPI.getPreferencesChoices(),
      ]);
      const loaded: RegionalDefaults = {
        default_currency: settingsRes.data.default_currency || 'GBP',
        default_timezone: settingsRes.data.default_timezone || 'Europe/London',
        default_date_format: settingsRes.data.default_date_format || 'DD/MM/YYYY',
      };
      setValues(loaded);
      setInitial(loaded);
      setChoices(choicesRes.data);
    } catch (err: unknown) {
      console.error('Failed to load platform regional defaults:', err);
      setError('Could not load the platform regional defaults. Check your connection and try again.');
      toast.error('Could not load regional defaults.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDefaults();
  }, [fetchDefaults]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings(values);
      setInitial(values);
      toast.success('Platform regional defaults saved.');
    } catch (err: unknown) {
      console.error('Failed to save platform regional defaults:', err);
      toast.error('Could not save regional defaults. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const update = (field: keyof RegionalDefaults, value: string) =>
    setValues((prev) => ({ ...prev, [field]: value }));

  const dirty =
    values.default_currency !== initial.default_currency ||
    values.default_timezone !== initial.default_timezone ||
    values.default_date_format !== initial.default_date_format;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Regional Defaults
        </CardTitle>
        <CardDescription>
          The platform-wide fallback for currency, timezone and date format. These apply only
          where a site has no override of its own and no group above it sets a value — a group
          default or a site override always wins.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="flex items-start gap-2 py-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>{error}</p>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="platform-default-currency">Currency</Label>
                <select
                  id="platform-default-currency"
                  value={values.default_currency}
                  onChange={(e) => update('default_currency', e.target.value)}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background"
                >
                  {choices?.currencies.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="platform-default-timezone">Timezone</Label>
                <select
                  id="platform-default-timezone"
                  value={values.default_timezone}
                  onChange={(e) => update('default_timezone', e.target.value)}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background"
                >
                  {choices?.timezones.map((tz) => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="platform-default-date-format">Date Format</Label>
                <select
                  id="platform-default-date-format"
                  value={values.default_date_format}
                  onChange={(e) => update('default_date_format', e.target.value)}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background"
                >
                  {choices?.date_formats.map((df) => (
                    <option key={df.value} value={df.value}>{df.label}</option>
                  ))}
                </select>
                <div className="text-xs text-muted-foreground">
                  Preview:{' '}
                  <span className="font-mono">{formatDatePreview(values.default_date_format)}</span>
                </div>
              </div>
            </div>

            <Button onClick={handleSave} disabled={!dirty || saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default RegionalDefaultsTab;
